import { useEffect, useRef, useState } from 'react';

// A photo that wipes open from the bottom when it first scrolls into view,
// then drifts a little slower than the page while it's on screen. The image
// is over-scaled inside its frame so the drift never shows an edge.
function ParallaxImage({ src, alt, caption, ratio, className = '', speed = 0.1 }) {
  const figureRef = useRef(null);
  const frameRef = useRef(null);
  const imgRef = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const figure = figureRef.current;
    const frame = frameRef.current;
    const img = imgRef.current;
    if (!figure || !frame || !img) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    // The figure, not the frame: the frame starts fully clipped for the
    // wipe-in, and a clipped element never counts as intersecting.
    observer.observe(figure);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return () => observer.disconnect();
    }

    let raf = 0;
    const update = () => {
      raf = 0;
      const rect = frame.getBoundingClientRect();
      const vh = window.innerHeight;
      if (rect.bottom < -100 || rect.top > vh + 100) return;
      const offset = (rect.top + rect.height / 2 - vh / 2) * -speed;
      img.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0) scale(1.16)`;
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [speed]);

  return (
    <figure ref={figureRef} className={`p-figure ${shown ? 'is-shown' : ''} ${className}`}>
      <div className="p-figure-frame" ref={frameRef} style={{ aspectRatio: ratio }}>
        <img ref={imgRef} src={src} alt={alt} loading="lazy" decoding="async" />
      </div>
      {caption && <figcaption className="p-figure-caption">{caption}</figcaption>}
    </figure>
  );
}

export default ParallaxImage;
